import React from 'react';
import moment from 'moment';
import makeStyles from '@mui/styles/makeStyles';
import { Done, DoneAll } from '@mui/icons-material';
import { grey, green } from '@mui/material/colors';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import { SmsDirection, SmsActivityStatus } from '@trnsact/trnsact-shared-types';
import { ActivityCardProps, SmsConversationData } from '../../types';
import { DealSmsActivity } from '../../api/types';
import { CardContainer } from './common/CardContainer';
import { DateFormats } from '../../../../constants/common';
import { generatePersonaFullName, getPersonaInitials } from 'utils/formatters';
import { useSelector } from 'react-redux';

interface Props extends ActivityCardProps<DealSmsActivity> {
  onOpenConversation?: (data: SmsConversationData) => void;
}

export const MessageCard = ({ entity, onOpenConversation }: Props) => {
  const currentUserProfile = useSelector((state: any) => state.userProfile);

  const isOutgoing = entity.smsDirection === SmsDirection.Outgoing;
  const isUnread = entity.smsDirection === SmsDirection.Incoming && entity.status !== SmsActivityStatus.Read;

  const { ownerPgCreditApplication } = entity;
  const contactName = generatePersonaFullName(ownerPgCreditApplication);
  const initials = getPersonaInitials(ownerPgCreditApplication.firstName, ownerPgCreditApplication.lastName);

  const senderName = generatePersonaFullName(entity.createdByUserProfile);

  const handleClick = () => {
    onOpenConversation?.({
      voId: entity.parentId,
      conversationSequenceNumber: entity.conversationSequenceNumber,
      contactName,
      contactId: entity.ownerPgCreditApplicationId ?? null,
      contactPhone: ownerPgCreditApplication.phone ?? null,
      contactTitle: ownerPgCreditApplication.title ?? null,
      contactInitials: initials,
    });
  };

  const classes = useStyles({ isUnread, isOutgoing });

  return (
    <CardContainer>
      <Box onClick={handleClick} sx={{ cursor: 'pointer' }}>
        {isUnread && <Box className={classes.unreadIndicator} />}
        <Stack gap="0.75rem">
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" gap="0.75rem" alignItems="center">
              <Avatar className={classes.avatar}>
                <Typography fontWeight={500}>{initials}</Typography>
              </Avatar>

              <Stack>
                <Stack direction="row" gap="0.5rem" alignItems="center">
                  <Typography variant="subtitle1" fontWeight="bold">
                    {contactName}
                  </Typography>

                  {ownerPgCreditApplication?.pc && (
                    <Box className={classes.primaryChip}>
                      <Typography
                        align="center"
                        component="span"
                        variant="subtitle2"
                        color="textSecondary"
                        sx={{ fontSize: '12px' }}
                      >
                        Primary
                      </Typography>
                    </Box>
                  )}
                </Stack>

                <Typography variant="caption" color="textSecondary">
                  {ownerPgCreditApplication?.phone}
                  {ownerPgCreditApplication?.title && ` • ${ownerPgCreditApplication.title}`}
                </Typography>
              </Stack>
            </Stack>

            {/*           {!!unreadCount && unreadCount > 0 && (
            <Badge
              badgeContent={`+${unreadCount}`}
              color="error"
              sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none' } }}
            />
          )} */}
          </Stack>

          <Box className={classes.messageBubble}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack flex={1} gap="0.25rem">
                <Typography variant="caption" fontWeight={500}>
                  {isOutgoing
                    ? currentUserProfile.id === entity.createdByUserProfileId
                      ? 'You'
                      : senderName
                    : contactName}
                </Typography>

                <Typography variant="body2">{entity.message}</Typography>
              </Stack>

              <Stack direction="row" alignItems="center" gap="0.25rem" alignSelf="flex-end">
                <Typography variant="caption" color={isOutgoing ? 'white' : 'textSecondary'}>
                  {moment(entity.createdDateTime).format(DateFormats.DateTimeMedium12Hour)}
                </Typography>

                {isOutgoing && entity.status === SmsActivityStatus.Sent && <Done className={classes.sentIcon} />}
                {isOutgoing && entity.status === SmsActivityStatus.Delivered && (
                  <DoneAll className={classes.sentIcon} />
                )}
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </CardContainer>
  );
};

const useStyles = makeStyles({
  unreadIndicator: {
    width: '4px',
    borderRadius: 2,
    backgroundColor: '#2196f3',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
  },
  avatar: {
    width: 40,
    height: 40,
    backgroundColor: '#90caf9',
    color: 'black',
    fontSize: '14px',
  },
  messageBubble: (props: { isUnread: boolean; isOutgoing: boolean }) => ({
    padding: '0.5rem 0.75rem',
    borderRadius: props.isOutgoing ? '20px 20px 1px 20px' : '20px 20px 20px 1px',
    backgroundColor: props.isOutgoing ? green[500] : grey[200],
    color: props.isOutgoing ? 'white' : 'inherit',
  }),
  sentIcon: {
    fontSize: 16,
    color: 'white',
  },
  primaryChip: {
    borderRadius: '6px',
    padding: '2px 6px',
    color: grey[700],
    backgroundColor: grey[200],
  },
});
